import React from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { AnimatePresence } from "framer-motion";
import { twMerge } from "tailwind-merge";

import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { PopoverProvider } from "src/base-components/Popover/PopoverContext";
import { HeadlessUIPlacement } from "src/utils/headlessui";

type Placement =
  | "top"
  | "top-end"
  | "top-start"
  | "bottom"
  | "bottom-end"
  | "bottom-start"
  | "right"
  | "right-end"
  | "right-start"
  | "left-end"
  | "left-start";

const getPlacement = (placement: Placement): HeadlessUIPlacement => {
  return placement.replace("-", " ") as HeadlessUIPlacement;
};

type Props = {
  button: React.ReactNode | ((props: { open: boolean }) => React.ReactElement);
  children: React.ReactNode;
  className?: string;
  wrapperClassName?: string;
  dataLoc?: string;
  // Can be deleted after using Floating Windows
  isOpen?: boolean;
  placement?: Placement;
  offsetX?: number;
  offsetY?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

export const CustomPopover: React.FC<Props> & {
  Button: typeof PopoverButton;
} = ({
  children,
  className,
  wrapperClassName,
  button,
  dataLoc,
  isOpen,
  placement = "bottom-end",
  offsetX = 10,
  offsetY,
  onMouseEnter,
  onMouseLeave,
}) => {
  const controlled = isOpen !== undefined;

  return (
    <Popover
      className={twMerge("h-full w-fit", wrapperClassName)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <PopoverButton
        as="span"
        className="focus:outline-none"
        data-loc={`${dataLoc}-button`}
        tabIndex={-1}
      >
        {button}
      </PopoverButton>
      <AnimatePresence>
        {((controlled && isOpen) || !controlled) && (
          <PopoverPanel
            anchor={{
              to: getPlacement(placement),
              padding: 8,
              gap: offsetX,
              offset: offsetY,
            }}
            as={MotionDropdownPanel}
            className={twMerge(
              "decideScrollbar z-50 rounded-lg border border-gray-200 bg-white shadow-xl",
              className,
            )}
            data-loc={dataLoc}
            static={controlled}
          >
            <PopoverProvider>{children}</PopoverProvider>
          </PopoverPanel>
        )}
      </AnimatePresence>
    </Popover>
  );
};

CustomPopover.Button = PopoverButton;
